/**
 * Audio context singleton.
 *
 * iOS Safari requires an AudioContext to be created (or resumed) inside a
 * user gesture handler, otherwise all playback is silently suppressed. We
 * lazily create the context on first user interaction, then expose `play`
 * helpers to the rest of the app.
 *
 * Two unlock paths cover the common cases:
 *   1. Explicit: `unlockAudio()` is called from high-signal buttons
 *      (Welcome "Start", Map tile tap) so those transitions work even
 *      for first-time visitors.
 *   2. Implicit: a one-time window pointerdown listener (installed by
 *      installAutoUnlock()) unlocks audio the first time the user taps
 *      ANYWHERE. This rescues deep-linked navigation and page refreshes
 *      where the child lands directly on a stage without coming through
 *      the Welcome screen.
 */

import { signal } from '@preact/signals';

export const audioUnlocked = signal(false);

let ctx: AudioContext | null = null;

function getCtor(): typeof AudioContext | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window.AudioContext ?? (window as any).webkitAudioContext) ?? null;
}

/**
 * Shared AudioContext accessor — every caller (lib/audio.ts, MicButton,
 * etc.) must use this so there's only ever one context on the page.
 * Returns null until unlockAudio() has run at least once inside a user
 * gesture (iOS Safari requirement).
 */
export function getAudioContext(): AudioContext | null {
  return ctx;
}

/** Call from a click/touch event handler — creates / resumes the context. */
export function unlockAudio(): void {
  if (audioUnlocked.value) return;
  try {
    const Ctor = getCtor();
    if (!Ctor) return;
    ctx = new Ctor();
    // Create a silent buffer and play it to unlock.
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
    audioUnlocked.value = true;
  } catch (err) {
    console.warn('[huroof] audio unlock failed:', err);
  }
}

let autoInstalled = false;
/**
 * Attach listeners that unlock audio on *any* user interaction. We stay
 * attached until unlockAudio() actually succeeds — iOS Safari sometimes
 * fails the first attempt, so single-shot listeners weren't enough.
 */
export function installAutoUnlock(): void {
  if (autoInstalled) return;
  autoInstalled = true;

  const tryUnlock = () => {
    unlockAudio();
    if (audioUnlocked.value) {
      window.removeEventListener('pointerdown', tryUnlock, true);
      window.removeEventListener('touchstart', tryUnlock, true);
      window.removeEventListener('click', tryUnlock, true);
      window.removeEventListener('keydown', tryUnlock, true);
    }
  };
  // Use capture phase so we fire before anything that may stopPropagation.
  window.addEventListener('pointerdown', tryUnlock, { capture: true, passive: true });
  window.addEventListener('touchstart', tryUnlock, { capture: true, passive: true });
  window.addEventListener('click', tryUnlock, { capture: true });
  window.addEventListener('keydown', tryUnlock, { capture: true });
}

export async function playUrl(url: string): Promise<void> {
  if (!ctx || !audioUnlocked.value) return;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      // If resume fails silently, the play call below will just be a no-op.
    }
  }
  try {
    const res = await fetch(url);
    if (!res.ok) return;
    const bytes = await res.arrayBuffer();
    const buffer = await ctx.decodeAudioData(bytes);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch (err) {
    console.warn('[huroof] playUrl failed:', err);
  }
}
